import React from 'react';
import {
	ContentState,
	convertFromHTML,
	Editor,
	EditorState,
	Modifier,
	RichUtils,
	type Editor as EditorType,
	type EditorProps,
	type EditorState as EditorStateType
} from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { useSpeechRecognition } from 'react-speech-recognition';
import { InputLabel, Typography } from '@mui/material';

import { type FieldProps } from './BaseTextField';
import EditorHtmlToolbar from './editor-html/toolbar/EditorHtmlToolbar';
import editorDecorator from './editor-html/ComponentPlugins';

import 'draft-js/dist/Draft.css';

type EditorHtmlProps = Pick<FieldProps<string>, 'name' | 'label' | 'value' | 'isEdit' | 'sendChange'>;

const styleOverrideFn = contentBlock => {
	const type = contentBlock.getType();

	switch (type) {
		case 'ordered-list-item':
			return 'editor-ol-item';
		case 'unordered-list-item':
		default:
			return 'editor-ul-item';
	}
};

/** A basic html editor with support for some markup elements */
export default function EditorHtml({ name, label, value, isEdit, sendChange }: EditorHtmlProps) {
	const { interimTranscript } = useSpeechRecognition({ clearTranscriptOnListen: false });

	const editorRef = React.createRef<EditorType>();

	const [editorState, setEditorState] = React.useState<EditorStateType>(() => {
		if (value) {
			const blocks = convertFromHTML(value);
			const defaultState = ContentState.createFromBlockArray(blocks.contentBlocks, blocks.entityMap);
			return EditorState.createWithContent(defaultState, editorDecorator);
		}

		return EditorState.createEmpty(editorDecorator);
	});

	const handleChange = state => {
		setEditorState(state);
	};

	const handleBlur = () => {
		sendChange(name, stateToHTML(editorState.getCurrentContent()));
	};

	const handleKeyCommand = (command, state) => {
		const newState = RichUtils.handleKeyCommand(state, command);

		if (newState) {
			setEditorState(newState);
			return 'handled';
		}

		return 'not-handled';
	};

	const handlePastedText: EditorProps['handlePastedText'] = (text, html, _editorState) => {
		const selection = editorState.getSelection();
		const currentContent = editorState.getCurrentContent();

		const updatedContent = selection.isCollapsed()
			? Modifier.insertText(currentContent, selection, text)
			: Modifier.replaceText(currentContent, selection, text);
		setEditorState(EditorState.push(_editorState, updatedContent, 'insert-characters'));

		return 'handled';
	};

	return (
		<>
			<InputLabel className="scale-75 text-13">{label}</InputLabel>
			{isEdit && <EditorHtmlToolbar editorState={editorState} setEditorState={setEditorState} />}
			<Typography component="div">
				<Editor
					ref={editorRef}
					editorState={editorState}
					handleKeyCommand={handleKeyCommand}
					readOnly={!isEdit}
					onChange={handleChange}
					onBlur={handleBlur}
					preserveSelectionOnBlur
					blockStyleFn={styleOverrideFn}
					handlePastedText={handlePastedText}
				/>
				<p>{`${interimTranscript}`}</p>
			</Typography>
		</>
	);
}
